import React, {useEffect, useMemo, useState} from "react";
import {Route} from "@/routing/Router";
import TimesheetPageHeading from "@widgets/reports/timesheet/heading/TimesheetPageHeading";
import TimesheetOverview from "@widgets/reports/timesheet/chart/TimesheetOverview";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {searchParamsToDateRange} from "@widgets/reports/Statistics";
import {DateRange} from "react-day-picker";
import {useSearchParams} from "react-router-dom";
import StatisticsRangePicker from "@widgets/reports/timesheet/range/StatisticsRangePicker";
import {DayItem, getDaysBetween} from "@/utils/DateUtils";
import {useQuery} from "@tanstack/react-query";
import {EmployeeInReport} from "@/data/entities/employee";
import ExportTimesheetModal from "@widgets/reports/timesheet/export/ExportTimesheetModal";
import {api} from "@/data/api";

type Props = {
    route: Route,
};

const EmployeesTimesheet = ({route}: Props) => {
    const {dashboardLocation} = useRestaurant();
    const [searchParams] = useSearchParams();
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const dateRange: DateRange = useMemo(() => searchParamsToDateRange(searchParams), [searchParams]);
    const daysBetween: DayItem[] = useMemo(() => getDaysBetween(dateRange), [dateRange]);
    const {data: fetchedData, isFetching} = useQuery({
        // Date range should not be undefined
        queryKey: [
            "timesheet-report",
            dashboardLocation.id,
            dateRange.from?.toISOString(),
            dateRange.to?.toISOString(),
        ],
        queryFn: () => api.timeEntry.getTimeEntriesReport(
            dateRange.from?.toISOString() || "",
            dateRange.to?.toISOString() || "",
            dashboardLocation.id,
        ),
        initialData: [],
    });
    const [data, setData] = useState<EmployeeInReport[]>([]);

    // We are placing the retrieved TanStack data in a state, so we can update
    // the data after updating a specific time entry.
    useEffect(() => {
        setData(fetchedData)
    }, [fetchedData, setData]);

    return (
        <>
            <TimesheetPageHeading
                route={route}
                dateRange={dateRange}
                setShowDatePicker={setShowDatePicker}
                setShowExportModal={setShowExportModal}
                dataLoading={isFetching}
            />

            <div className={"mt-6 md:mt-12"}>
                <TimesheetOverview
                    daysBetween={daysBetween}
                    dateRange={dateRange}
                    data={data}
                    setData={setData}
                />
            </div>

            <StatisticsRangePicker
                showDatePicker={showDatePicker}
                dateRange={dateRange}
                setShowDatePicker={setShowDatePicker}
            />

            <ExportTimesheetModal
                dateRange={dateRange}
                show={showExportModal}
                setShow={setShowExportModal}
                data={data}
            />
        </>
    );
};

export default EmployeesTimesheet;